import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, ButtonBase, Typography, IconButton, Tooltip, alpha, Skeleton } from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Palette as PaletteIcon,
  WarningAmber as WarningIcon,
  Extension as ModulesIcon,
} from "@mui/icons-material";
import {
  FaClinicMedical,
  FaUsers,
  FaCoins,
  FaChartLine,
  FaUserInjured,
} from "react-icons/fa";
import useAuth from "../../../hooks/useAuth";
import { useGetClinicSummaryMutation, useHardDeleteClinicMutation } from "../../../store/clinicApi";
import { useAlert } from "../../../context/AlertProvider";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { generatePalette } from "../../../theme/themeUtils";
import { DEFAULT_PRIMARY } from "../../../theme/palettes";
import { URL } from "../../../store/utils";
import { ClinicThemeContext } from "../../../theme/ClinicThemeProvider";
import { ActionButton, FormTextField } from "../../../components/atoms";
import EmptyState from "../../../components/atoms/EmptyState";
import { EntityHeader } from "../../../components/molecules";
import { FormDialog } from "../../../components/organisms";
import { StatusPill } from "../../../components/organisms/ClinicRecordList";
import StaffTab from "./StaffTab";
import PatientsTab from "./PatientsTab";
import TokenTab from "./TokenTab";
import ClinicInfoTab from "./ClinicInfoTab";
import WorkingHoursTab from "./WorkingHoursTab";
import AppearanceTab from "./AppearanceTab";
import ModulesTab from "./ModulesTab";
import { profilesLabel } from "../../../constants/clinicModules";

// Düz (kutusuz) sekmeler: tek veri akışı — beyaz zemin, kendi araç satırı/tablosu.
const FLAT_TABS = new Set(["personel", "hastalar"]);

const TABS = [
  { key: "klinik-bilgileri", label: "Klinik Bilgileri", icon: <EditIcon sx={{ fontSize: 18 }} /> },
  { key: "personel", label: "Personel ve Roller", icon: <FaUsers size={15} /> },
  { key: "hastalar", label: "Hastalar", icon: <FaUserInjured size={15} /> },
  { key: "token", label: "Token Ayarı", icon: <FaCoins size={15} /> },
  { key: "token-performans", label: "Token Performansı", icon: <FaChartLine size={15} /> },
  { key: "calisma-saatleri", label: "Çalışma Saatleri", icon: <ScheduleIcon sx={{ fontSize: 18 }} /> },
  { key: "gorunum", label: "Görünüm", icon: <PaletteIcon sx={{ fontSize: 18 }} /> },
  { key: "moduller", label: "Modüller", icon: <ModulesIcon sx={{ fontSize: 18 }} /> },
];

export default function ClinicDetail() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [getClinicSummary, { data: summaryData, isLoading: isFetching }] = useGetClinicSummaryMutation();
  const [hardDeleteClinic, { isLoading: isDeleting }] = useHardDeleteClinicMutation();
  const { showSuccess, showError } = useAlert();

  const isStage = process.env.REACT_APP_ENV === "stage" || process.env.NODE_ENV === "development";

  const handleOpenDeleteDialog = () => {
    setDeletePassword("");
    setDeleteError("");
    setDeleteDialogOpen(true);
  };

  const handleDeleteClinic = async () => {
    setDeleteError("");
    if (!isStage && !deletePassword) {
      setDeleteError("Şifre gerekli");
      return;
    }
    try {
      await hardDeleteClinic({
        token,
        clinicId,
        password: isStage ? undefined : deletePassword,
      }).unwrap();
      showSuccess("Klinik ve tüm bağlı veriler başarıyla silindi");
      setDeleteDialogOpen(false);
      navigate("/Klinikler");
    } catch (error) {
      const msg = error?.data?.message || "Klinik silinirken bir hata oluştu";
      setDeleteError(msg);
      showError(msg);
    }
  };

  const refreshSummary = useCallback(() => {
    if (token && clinicId) getClinicSummary({ token, clinicId });
  }, [token, clinicId, getClinicSummary]);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  // Token bakiyesi — tüm tab'larda görünen header için
  const [balance, setBalance] = useState(null);
  const refreshBalance = useCallback(async () => {
    if (!token || !clinicId) return;
    try {
      const res = await fetch(
        `${String(URL.url || "").replace(/\/+$/, "")}/api/admin/tokens/balance`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ clinicId }),
        }
      );
      const json = await res.json();
      const raw = json?.data?.balance ?? json?.balance;
      // raw can be a number (0) or an object { balance, totalLoaded, totalConsumed }
      let value = 0;
      if (typeof raw === "number") value = raw;
      else if (raw && typeof raw === "object") value = Number(raw.balance) || 0;
      else value = Number(raw) || 0;
      setBalance(value);
    } catch (_e) { /* sessizce yut */ }
  }, [token, clinicId]);
  useEffect(() => { refreshBalance(); }, [refreshBalance]);

  const clinic = useMemo(
    () => summaryData?.data?.clinic || null,
    [summaryData]
  );

  const activeTab = searchParams.get("tab") || "klinik-bilgileri";
  const setActiveTab = (tab) => {
    setRefreshKey((k) => k + 1);
    setSearchParams({ tab }, { replace: true });
  };

  const clinicPalette = useMemo(
    () => generatePalette(clinic?.themeColor || DEFAULT_PRIMARY),
    [clinic?.themeColor]
  );
  const headerThemeCtx = useMemo(
    () => ({
      palette: clinicPalette,
      clinicColor: clinic?.themeColor || null,
      isCustomTheme: Boolean(clinic?.themeColor),
    }),
    [clinicPalette, clinic?.themeColor]
  );

  const rawLogoSrc = clinic?.logoIconUrl || "";
  const normalizedLogoSrc = typeof rawLogoSrc === "string" ? rawLogoSrc.replace(/^\/+assets\//, "/assets/") : rawLogoSrc;
  const baseUrl = String(URL.url || "").replace(/\/+$/, "");
  const logoSrc =
    typeof normalizedLogoSrc === "string" && normalizedLogoSrc.startsWith("/assets/")
      ? `${baseUrl}${normalizedLogoSrc}`
      : normalizedLogoSrc;

  if (!clinic && !isFetching) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <EmptyState
          variant="inline"
          icon={<FaClinicMedical />}
          title="Klinik bulunamadı"
          action={
            <IconButton onClick={() => navigate("/Klinikler")} aria-label="Geri">
              <ArrowBackIcon />
            </IconButton>
          }
        />
      </Box>
    );
  }

  const isActive = clinic ? clinic.status !== "inactive" : true;
  const profileText = profilesLabel(clinic?.profiles);

  const headerChips = clinic
    ? [
        <StatusPill
          key="status"
          label={isActive ? "Aktif" : "Pasif"}
          tone={isActive ? "success" : "neutral"}
          palette={clinicPalette}
        />,
      ]
    : [<Skeleton key="status" variant="rounded" width={48} height={22} sx={{ borderRadius: 1.5 }} />];

  // Alt satır meta: "#1042 · info@klinik.com · Diyet kliniği"
  const headerMeta = clinic
    ? [clinic.puserNo != null ? `#${clinic.puserNo}` : null, clinic.email, profileText].filter(Boolean).join(" · ")
    : null;
  const headerSubtitle = !clinic ? (
    <Skeleton component="span" variant="text" width={220} height={16} sx={{ display: "inline-block" }} />
  ) : headerMeta ? (
    <Box component="span" sx={{ color: COLORS.slate500, fontSize: TYPO.caption, fontVariantNumeric: "tabular-nums" }}>
      {headerMeta}
    </Box>
  ) : null;

  const headerActions = (
    <>
      {/* Token bakiyesi — tıklanınca Token Performansı sekmesi */}
      <ButtonBase
        onClick={() => setActiveTab("token-performans")}
        aria-label="Token performansını aç"
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          "&:hover": { bgcolor: COLORS.slate50 },
        }}
      >
        <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, lineHeight: 1.3 }}>Token bakiyesi</Typography>
        {balance === null ? (
          <Skeleton variant="text" width={72} height={24} />
        ) : (
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: COLORS.slate800, lineHeight: 1.4, fontVariantNumeric: "tabular-nums" }}>
            {balance.toLocaleString("tr-TR")}
          </Typography>
        )}
      </ButtonBase>

      {/* Kalıcı silme — yalnız pasif klinikte */}
      <Tooltip
        title={isActive ? "Silmek için önce Klinik Bilgileri'nden pasif duruma alın" : "Kliniği ve bağlı tüm verileri kalıcı olarak sil"}
        arrow
      >
        <span>
          <ActionButton
            variant="delete"
            tone="outline"
            compact
            label="Kliniği Sil"
            onClick={handleOpenDeleteDialog}
            disabled={isActive || !clinic}
            sx={{ flexShrink: 0 }}
          />
        </span>
      </Tooltip>
    </>
  );

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: COLORS.slate50 }}>
      {/* Header — sekme göstergesi ve geri butonu klinik temasıyla boyansın diye
          EntityHeader klinik paletiyle sarılır */}
      <ClinicThemeContext.Provider value={headerThemeCtx}>
        <EntityHeader
          onBack={() => navigate("/Klinikler")}
          avatar={{
            src: logoSrc || undefined,
            initials: clinic ? (clinic.name || "K")[0].toUpperCase() : undefined,
            icon: <FaClinicMedical />,
            alt: clinic?.name,
          }}
          title={clinic ? clinic.name : <Skeleton variant="text" width={200} height={28} />}
          subtitle={headerSubtitle}
          chips={headerChips}
          actions={headerActions}
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </ClinicThemeContext.Provider>

      {/* Tab Content */}
      {/* Düz sekmeler (tek veri akışı) beyaz ve kenardan kenara; kartlılar kendi
          CARD_PAGE_PAD + slate50 kabını çizer (design-principles §2, §8.1) */}
      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0, bgcolor: FLAT_TABS.has(activeTab) ? COLORS.white : COLORS.slate50 }}>
        {activeTab === "personel" && <StaffTab key={refreshKey} clinic={clinic} clinicId={clinicId} token={token} palette={clinicPalette} onSave={refreshSummary} />}
        {activeTab === "hastalar" && <PatientsTab key={refreshKey} clinicId={clinicId} token={token} palette={clinicPalette} />}
        {activeTab === "token" && <TokenTab key={`${refreshKey}-ayar`} section="settings" clinicId={clinicId} token={token} palette={clinicPalette} clinic={clinic} onBalanceChange={refreshBalance} />}
        {activeTab === "token-performans" && <TokenTab key={`${refreshKey}-performans`} section="performance" clinicId={clinicId} token={token} palette={clinicPalette} clinic={clinic} onBalanceChange={refreshBalance} />}
        {activeTab === "klinik-bilgileri" && <ClinicInfoTab key={refreshKey} clinicId={clinicId} token={token} palette={clinicPalette} onSave={refreshSummary} />}
        {activeTab === "calisma-saatleri" && <WorkingHoursTab key={refreshKey} clinicId={clinicId} token={token} palette={clinicPalette} />}
        {activeTab === "moduller" && <ModulesTab key={refreshKey} clinicId={clinicId} token={token} palette={clinicPalette} onSave={refreshSummary} />}
        {activeTab === "gorunum" && <AppearanceTab key={refreshKey} clinicId={clinicId} token={token} palette={clinicPalette} onSave={refreshSummary} />}
      </Box>

      {/* Klinik Hard Delete Dialog */}
      <FormDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        icon={<WarningIcon />}
        title="Kliniği Kalıcı Olarak Sil"
        maxWidth="xs"
        loading={isDeleting}
        onSubmit={handleDeleteClinic}
        submitDisabled={!isStage && !deletePassword}
        submitColor="error"
        submitLabel="Kalıcı Olarak Sil"
        loadingLabel="Siliniyor..."
        cancelLabel="Vazgeç"
      >
        <Box sx={{
          bgcolor: alpha(COLORS.errorDark, 0.06),
          border: `1px solid ${alpha(COLORS.errorDark, 0.15)}`,
          borderRadius: 2,
          p: 2,
        }}>
          <Typography variant="body2" color={COLORS.slate700} fontWeight={500}>
            <strong>"{clinic?.name || ""}"</strong> kliniği ve tüm bağlı veriler (kullanıcılar, randevular, tedaviler, tetkikler, ilaçlar) kalıcı olarak silinecektir.
          </Typography>
          <Typography variant="body2" color={COLORS.error} fontWeight={700} sx={{ mt: 1 }}>
            Bu işlem geri alınamaz!
          </Typography>
        </Box>

        {!isStage && (
          <FormTextField
            label="Şifreniz"
            type="password"
            fullWidth
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            autoComplete="current-password"
          />
        )}

        {deleteError && (
          <Typography variant="body2" color={COLORS.error} sx={{ fontWeight: 600 }}>
            {deleteError}
          </Typography>
        )}
      </FormDialog>
    </Box>
  );
}
